import {compareTwoStrings, escapeRegexp} from '../guided/backends/utils';
import type {GuidedV2LinkAttribute, GuidedV2LinkTag} from './models/quick-link';
import type {GuidedV2SectionSource} from './models/models';
import {h,type JSX} from '@stencil/core';
import { Suggestion } from '../guided/subcomponents/suggestion';
import type {GetStatusTag, QdsImportance} from './models/ui';

export function splitStringWithDelimiter(str: string, delimiter: string): string[] {
    const escapedDelimiter = escapeRegexp(delimiter);
    const regex = new RegExp(`(${escapedDelimiter})`, 'gui');
    return str.split(regex).filter(Boolean);
}

export function splitByDelimiters(str: string, delimiters: string[]): string[] {
    const escapedDelimiters = delimiters.map(d => escapeRegexp(d)).join('|');
    return str.split(new RegExp(`(${escapedDelimiters})`, 'gui')).filter(Boolean);
}

export const onlyMatchingSource = (sourceSections: GuidedV2SectionSource[]) => (item: {
  category: string,
  searcher: string,
}) => {
    return item && sourceSections.some(base => {
        return base.category == item.category && base.searcher == item.searcher;
    });
};
export const sortTagByImportance = (
  a: GuidedV2LinkTag,
  b: GuidedV2LinkTag,
) => {
    if (a.importance == 'emphasized')
      return -1;
    if (b.importance == 'emphasized')
      return 1;
    return 0;
};
export const sortAttributeByImportance = (
  a: GuidedV2LinkAttribute,
  b: GuidedV2LinkAttribute,
) => {
    if (a.importance == 'emphasized')
      return -1;
    if (b.importance == 'emphasized')
      return 1;
    return 0;
};

const _shouldHighlightPartial = (
  input: string,
  searchTerm: string,
  highlightThreshold: number,
)=>{

    if(!input || !searchTerm)
      return false;

    const trimmed = searchTerm.trim();
    const score = compareTwoStrings(trimmed, input);

    if (score > highlightThreshold)
      return true;

    return input.toLowerCase().startsWith(trimmed);
};


const minimalHighlightingLength = 3;
/**
 * Check if the input should be highlighted
 * @param highlightThreshold
 * @param searchTerm
 */
export const shouldHighlightCalculator = (
  highlightThreshold: number,
  searchTerm: string,
) => (input: string): boolean => {

    if (searchTerm.length < minimalHighlightingLength)
      return false;

    if (input?.includes('class="highlight"')) return false;

    if(searchTerm.includes(' ')){
      return searchTerm
        .split(' ')
        .map(part => part.trim())
        .filter(part=>part.length >= minimalHighlightingLength)
        .some((part) => _shouldHighlightPartial(input, part, highlightThreshold));
    }
    return _shouldHighlightPartial(input, searchTerm, highlightThreshold);
};
/**
 * Sort results by decreasing score
 * @param resultA
 * @param resultB
 */
export const compareResultsByScore = (
  resultA: { score: number },
  resultB: { score: number },
) => {
    return resultB.score - resultA.score;
};

/**
 * Converts a string with "<span class='highlight'></span>" tags into parts with HTML JSX Elements
 * @param input String which contains parts to be highlighted
 * @returns An array of string and JSX elements, substituting highlighted part of the string with JSX span elements with the 'highlight' class
 */
export const parseHighlightedIntoJsx = (input: string): (string | JSX.Element)[]=> {
    const regex = /<span class="highlight">([\s\S]*?)<\/span>/gi;

    const parseContent = (text: string): (string | JSX.Element)[] => {
        const parts: (string | JSX.Element)[] = [];
        let lastIndex = 0;
        let match;

        while ((match = regex.exec(text)) !== null) {
          if (lastIndex < match.index) {
            parts.push(text.slice(lastIndex, match.index));
          }
          parts.push(
            <span class="highlight">{match[1]}</span>
          );
          lastIndex = regex.lastIndex;
        }
        if (lastIndex < text.length) {
          parts.push(text.slice(lastIndex));
        }
        return parts;
    };

    return parseContent(input);
};


export const markHighlights = (input: string, wrapper: string, highlightClass: string) => {


};

export const promotedData = {
  popularSearchers: {
    fr: {
      fr: [
        {
          label:'Configurateur',
          name:'Configurateur',
        }, {
          label: 'Disjoncteur',
          name: 'Disjoncteur'
        }, {
          label: 'Catalogue',
          name: 'Catalogue' 
        }, {
          label: 'Odace',
          name: 'Odace'
        }, {
          label: 'Wiser',
          name: 'Wiser' 
        }, {
          label: 'nsx',
          name: 'nsx'
        }, {
          label: 'ic60n',
          name: 'ic60n' 
        }],
    },
  },
  selectedSuggestions: {
    fr: {
      fr: [{
        suggestionTitle: 'Disjoncteurs et appareillage modulaire',
        suggestionDescription: 'L\'offre d\'appareillages modulaires et de disjoncteurs de Schneider Electric propose une gamme complète de produits destinés à la protection, à la commande et à la...',
        suggestionURL: 'https://www.se.com/fr/fr/product-category/1600-disjoncteurs-et-appareillage-modulaire',
        suggestionImage: 'https://download.schneider-electric.com/files?p_Doc_Ref=PB120902_new&p_File_Type=rendition_369_jpg&default_image=DefaultProductImage.png',
      },
      {
        suggestionTitle: 'Bienvenue sur eDesign Tertiaire',
        suggestionDescription: 'Cette application est destinée à aider les professionnels de l\'électricité à concevoir des tableaux électriques pour les petits bâtiments à usage professionnel.',
        suggestionURL: 'https://edesign-630.se.com/home',
        suggestionImage: 'https://download.schneider-electric.com/files?p_Doc_Ref=eDesign_image&p_File_Type=rendition_369_jpg&default_image=DefaultProductImage.png',
      }],
    },
  }
};

export const getImportance = (importance:QdsImportance):string => {
    switch(importance){
    case 'emphasized':
      return 'emphasized';
    default:
    case 'standard':
      return 'standard';
    }
};


export const getStatusTag = (commStatus: string): GetStatusTag=> {
    switch (commStatus) {
    case '03':
      return [
        { type: 'RANGE_END_OF_SALE',
          text: 'Discontinued',
          status: 'error' }
      ];
    case'04':
      return [
        { type: 'RANGE_COMING_SOON',
          text: 'Coming soon',
          status: 'success' }
      ];
    case '05':
      return [
        { type: 'RANGE_TO_BE_DISCONTINUED',
          text: 'To be discontinued',
          status: 'warning' }
      ];
    case '06':
      return [
        { type: 'PRODUCT_COMING_SOON',
          text: 'Coming soon',
          status: 'success' },
        { type: 'PRODUCT_LAUNCHING',
          text: 'Launching',
          status: 'success' }
      ];
    case '14':
      return [
        { type: 'PRODUCT_TO_BE_DISCONTINUED',
          text: 'To be discontinued',
          status: 'warning' }
      ];
    case '16':
      return [
        { type: 'PRODUCT_RESTRICTED_SALES_FOR_SERVICES',
          text: 'Discontinued - Service only',
          status: 'error' },
        { type: 'PRODUCT_END_OF_SERVICE',
          text: 'To be end of service',
          status: 'warning' }
      ];
    case '18':
      return [
        { type: 'PRODUCT_END_OF_SALE',
          text: 'Discontinued',
          status: 'error' }
      ];
    case '19':
      return [
        { type: 'PRODUCT_END_OF_SALE_BLOCK',
          text: 'Discontinued',
          status: 'error' }
      ];
    default:
      return [];
    }
};
